import { useCallback, useMemo } from "react";
import { useDashboardVisibilityQuery } from "@/stores/queries/dashboard.query";
import { IS_HUB_PREVIEW, useHubPreviewStore } from "@/preview/hubPreview";
import { useAuth } from "@/contexts/AuthContext";
import { ROLES_LEVELS } from "@/common/permissions";
import {
  DASHBOARD_PAGE_MIN_ROLE,
  DASHBOARD_PAGE_ORDER,
  DASHBOARD_PAGE_ROUTES,
  DASHBOARD_PAGES,
  type DashboardMetricKey,
  type DashboardPageKey,
  type DashboardReportKey,
} from "../visibility";

export interface DashboardVisibilityState {
  /** True until the visibility settings have loaded; render a placeholder so hidden items never flash. */
  readonly isLoading: boolean;
  readonly isPageVisible: (page: DashboardPageKey) => boolean;
  readonly isMetricVisible: (metric: DashboardMetricKey) => boolean;
  /** False when the report, or the whole Reports page, is hidden. */
  readonly isReportVisible: (report: DashboardReportKey) => boolean;
  /** Route of the first page the merchant can see, or null when every page is hidden. */
  readonly firstVisibleRoute: string | null;
}

/**
 * Which dashboard pages and Overview metrics the Mezza Hub left visible for this merchant.
 * If the settings fail to load, everything is treated as visible: the server still enforces the
 * hidden items (404s and nulls), so failing open mostly risks an empty card. The exception is
 * values the app derives itself (visits per member from orders and members), which only this
 * hook hides.
 *
 * Inside the Hub's preview, the Hub's draft settings replace the saved ones (the server then hides nothing).
 *
 * Pages in `DASHBOARD_PAGE_MIN_ROLE` also count as hidden for users below that role, so the sidebar, the route
 * guard and the fallback landing page all skip them.
 */
export function useDashboardVisibility(): DashboardVisibilityState {
  const previewVisibility = useHubPreviewStore((state) => state.visibility);
  const query = useDashboardVisibilityQuery({ enabled: !IS_HUB_PREVIEW });
  const source = IS_HUB_PREVIEW ? previewVisibility : query.data;
  const hiddenPagesList = source?.hiddenPages;
  const hiddenMetricsList = source?.hiddenMetrics;
  const hiddenReportsList = source?.hiddenReports;
  const { user } = useAuth();
  const userRole = user?.role;

  const hiddenPages = useMemo(() => new Set(hiddenPagesList ?? []), [hiddenPagesList]);
  const hiddenMetrics = useMemo(() => new Set(hiddenMetricsList ?? []), [hiddenMetricsList]);
  const hiddenReports = useMemo(() => new Set(hiddenReportsList ?? []), [hiddenReportsList]);

  const isPageVisible = useCallback(
    (page: DashboardPageKey) => {
      if (hiddenPages.has(page)) return false;
      const minRole = DASHBOARD_PAGE_MIN_ROLE[page];
      return !minRole || (userRole !== undefined && ROLES_LEVELS[userRole] <= ROLES_LEVELS[minRole]);
    },
    [hiddenPages, userRole]
  );
  const isMetricVisible = useCallback((metric: DashboardMetricKey) => !hiddenMetrics.has(metric), [hiddenMetrics]);
  const isReportVisible = useCallback(
    (report: DashboardReportKey) => isPageVisible(DASHBOARD_PAGES.reports) && !hiddenReports.has(report),
    [isPageVisible, hiddenReports]
  );

  const firstVisibleRoute = useMemo(() => {
    const page = DASHBOARD_PAGE_ORDER.find(isPageVisible);
    return page ? DASHBOARD_PAGE_ROUTES[page] : null;
  }, [isPageVisible]);

  return {
    isLoading: IS_HUB_PREVIEW ? previewVisibility === null : query.isLoading,
    isPageVisible,
    isMetricVisible,
    isReportVisible,
    firstVisibleRoute,
  };
}
